import React from 'react';
import Select, { GroupBase, SingleValue, StylesConfig } from 'react-select';
import {
  IEmpresaAuditoria,
  IPeriodoAuditoria,
  TributoAuditoria,
} from '../interfaces/IAuditoria';

interface IOptionEmpresa {
  value: number;
  label: string;
}

interface IOptionTributo {
  value: TributoAuditoria;
  label: string;
}

// Pesquisável por digitação (react-select já filtra pelo texto digitado por padrão) — pedido
// explícito para as listas de Empresa e Tipo de Auditoria, que podem ter dezenas/centenas de
// itens e obrigavam o usuário a rolar a lista inteira para encontrar o que precisava.
// Estilo alinhado ao .auditoria-select (auditoria.css): altura 42px, borda e cor de foco iguais.
const estiloSelectPesquisavel: StylesConfig<any, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.isFocused ? '#005E96' : '#d7dce5',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(0,94,150,.12)' : 'none',
    '&:hover': { borderColor: '#005E96' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

interface Props {
  empresas: IEmpresaAuditoria[];
  periodos: IPeriodoAuditoria[];
  empresaId?: number;
  periodo?: string;
  tributo: TributoAuditoria | '';
  onEmpresaChange: (empresaId: number | undefined) => void;
  onPeriodoChange: (periodo: string | undefined) => void;
  onTributoChange: (tributo: TributoAuditoria | '') => void;
  modoSomenteTodos?: boolean;
  // Trial 72h: quando informado, o seletor de "Tipo de Auditoria" só lista 'TODOS' + os tributos
  // presentes nesta lista — os demais ficam fora do dropdown (não aparecem, não é só disabled).
  tributosPermitidos?: TributoAuditoria[];
}

const TRIBUTOS_FISCAIS: { value: TributoAuditoria; label: string }[] = [
  { value: 'IRPJ', label: 'IRPJ' },
  { value: 'CSLL', label: 'CSLL' },
  { value: 'PIS', label: 'PIS' },
  { value: 'COFINS', label: 'COFINS' },
];

const OUTRAS_AUDITORIAS: { value: TributoAuditoria; label: string }[] = [
  // Não é uma auditoria com resultado próprio — ao ser selecionado, redireciona para a Central de
  // Entrada de Dados (ver AuditoriaPage.tsx → selecionarTributo). Mantido no topo da lista,
  // imediatamente acima de SALDOS BANCÁRIOS, por ser o insumo (extrato bancário) que essa
  // auditoria usa para a conciliação (seção 3 do Épico "Central de Entrada de Dados").
  { value: 'EXTRATO_BANCARIO', label: 'EXTRATO BANCÁRIO' },
  { value: 'SALDOS_BANCARIOS', label: 'SALDOS BANCÁRIOS' },
  { value: 'DEPRECIACAO_AMORTIZACAO', label: 'DEPRECIAÇÃO / AMORTIZAÇÃO / EXAUSTÃO' },
  { value: 'AUDITORIA_CAIXA', label: 'CAIXA / ESPÉCIE / BENEFÍCIO INDIRETO' },
  { value: 'AUDITORIA_FINANCEIRA', label: 'RESGATE E IRRF SOBRE APLICAÇÕES FINANCEIRAS' },
  { value: 'AUDITORIA_ZERAMENTO_13', label: 'ZERAMENTO E PROVISÃO DE 13º SALÁRIO' },
  { value: 'AUDITORIA_BAIXA_PATRIMONIAL', label: 'BAIXA DIRETA DE ATIVO IMOBILIZADO' },
  { value: 'AUDITORIA_ADIANTAMENTO', label: 'COMPETÊNCIA DE ADIANTAMENTOS (FORNECEDORES)' },
  { value: 'AUDITORIA_ADIANTAMENTO_CLIENTES', label: 'PASSIVO — ADIANTAMENTOS DE CLIENTES' },
  { value: 'AUDITORIA_DESPESAS_ANTECIPADAS', label: 'AMORTIZAÇÃO — DESPESAS ANTECIPADAS' },
  { value: 'AUDITORIA_AJUSTE_EXERCICIOS_ANTERIORES', label: 'AJUSTES DE EXERCÍCIOS ANTERIORES' },
  { value: 'AUDITORIA_CONTAS_PAGAR_RECEBER', label: 'CONTAS A PAGAR E A RECEBER' },
  { value: 'AUDITORIA_FORNECEDORES_CONCILIACAO', label: 'FORNECEDORES (CONCILIAÇÃO)' },
  { value: 'AUDITORIA_RECEITAS_DIFERIDAS', label: 'RECEITAS DIFERIDAS (SEGREGAÇÃO TRIBUTÁRIA)' },
  { value: 'AUDITORIA_INTEGRIDADE_LANCAMENTOS', label: 'INTEGRIDADE DE LANÇAMENTOS (AJUSTES)' },
  { value: 'AUDITORIA_ADIANTAMENTO_SOCIOS', label: 'ADIANTAMENTOS A SÓCIOS (CARACTERIZAÇÃO DE REMUNERAÇÃO)' },
  { value: 'AUDITORIA_BENEFICIOS_INDIRETOS', label: 'BENEFÍCIOS INDIRETOS A SÓCIOS' },
  { value: 'AUDITORIA_RASTREABILIDADE_BANCARIA', label: 'RASTREABILIDADE BANCÁRIA (RAZÃO SOCIAL E DOCUMENTO)' },
  { value: 'AUDITORIA_ARREDONDAMENTOS_AJUSTES', label: 'ARREDONDAMENTOS E LANÇAMENTOS DE AJUSTES' },
  { value: 'AUDITORIA_ADIANTAMENTO_FUNCIONARIOS', label: 'ADIANTAMENTOS E EMPRÉSTIMOS A FUNCIONÁRIOS' },
  { value: 'AUDITORIA_ADIANTAMENTO_SALARIO', label: 'ADIANTAMENTO DE SALÁRIOS' },
  { value: 'AUDITORIA_ANOMALIA_TARIFA_BANCARIA', label: 'ANOMALIAS EM TARIFAS BANCÁRIAS' },
  { value: 'AUDITORIA_CONTA_TRANSITORIA', label: 'CONCILIAÇÃO DE CONTAS TRANSITÓRIAS' },
  { value: 'AUDITORIA_ADIANTAMENTO_VIAGEM', label: 'ADIANTAMENTOS DE VIAGENS' },
  { value: 'AUDITORIA_CLASSIFICACAO_RECEITA', label: 'CLASSIFICAÇÃO DE CONTAS DE RECEITA' },
  { value: 'AUDITORIA_PROVISAO_PIS_COFINS', label: 'PROVISÃO MENSAL DE PIS/COFINS' },
  { value: 'AUDITORIA_PROVISAO_ISS', label: 'PROVISÃO MENSAL DE ISS' },
  { value: 'AUDITORIA_PROVISAO_ICMS', label: 'PROVISÃO MENSAL DE ICMS' },
  { value: 'AUDITORIA_DUPLICIDADE_PAGAMENTOS', label: 'ERROS DE PERDA FINANCEIRA (DUPLICIDADE DE PAGAMENTOS)' },
  { value: 'AUDITORIA_ROTATIVIDADE_PROVISOES', label: 'ROTATIVIDADE DE PROVISÕES (CONTROLE DE ESTAGNAÇÃO)' },
  { value: 'AUDITORIA_PDD_ANALITICA', label: 'PDD ANALÍTICA (PARTES RELACIONADAS VS. TERCEIROS)' },
  { value: 'AUDITORIA_PEJOTIZACAO', label: 'GOVERNANÇA E DETECÇÃO DE PEJOTIZAÇÃO' },
  { value: 'AUDITORIA_LANCAMENTO_MANUAL', label: 'LANÇAMENTOS COM PALAVRA-CHAVE "MANUAL"' },
  { value: 'AUDITORIA_LANCAMENTO_ESTORNO', label: 'LANÇAMENTOS COM PALAVRA-CHAVE "ESTORNO"' },
  { value: 'AUDITORIA_LANCAMENTO_DIA_NAO_UTIL', label: 'LANÇAMENTOS EM FINAIS DE SEMANA E FERIADOS' },
  { value: 'AUDITORIA_DESPESA_ANALITICA_RELEVANTE', label: 'CONTAS ANALÍTICAS DE DESPESA (RFB 3.01/3.02)' },
  { value: 'AUDITORIA_LANCAMENTO_CORRETIVO', label: 'LANÇAMENTOS CORRETIVOS NO 1º DIA DO MÊS/1º DIA ÚTIL (REVERSÃO/AJUSTE)' },
  { value: 'AUDITORIA_SERVICOS_TERCEIROS_PF', label: 'SERVIÇOS DE TERCEIROS — PESSOA FÍSICA (RPA)' },
  { value: 'AUDITORIA_SOCIETARIA', label: 'AUDITORIA SOCIETÁRIA — CAPITAL SOCIAL (JUCESP)' },
  { value: 'AUDITORIA_QUALIDADE_HISTORICOS', label: 'QUALIDADE DE HISTÓRICOS DE LANÇAMENTOS' },
  { value: 'AUDITORIA_TRANSFER_PRICING', label: 'TRANSFER PRICING E INTERCOMPANY' },
  { value: 'AUDITORIA_CAMBIAL', label: 'EXPOSIÇÃO CAMBIAL E EFICIÊNCIA DE HEDGE' },
  { value: 'AUDITORIA_ROUND_TRIPPING', label: 'ROUND-TRIPPING (SIMULAÇÃO DE RECEITA)' },
  { value: 'AUDITORIA_CASH_RICH', label: 'INEFICIÊNCIA FINANCEIRA (CASH-RICH COM DÍVIDA CARA)' },
  { value: 'AUDITORIA_MUTACAO_VOLATIL', label: 'MUTAÇÃO VOLÁTIL — IMOBILIZADO VS. DESPESAS DE MANUTENÇÃO' },
  { value: 'AUDITORIA_CREDITO_ESTAGNADO', label: 'CRÉDITOS FISCAIS ESTAGNADOS — RISCO DE IMPAIRMENT (CPC 01)' },
  { value: 'AUDITORIA_PROVISAO_ESPELHO', label: 'PROVISÕES "ESPELHO" — COOKIE JAR ACCOUNTING (CPC 25)' },
  { value: 'AUDITORIA_CORRELACAO_RECEITA', label: 'CORRELAÇÃO RECEITA BRUTA / REDUTORAS — INCOME SMOOTHING' },
  { value: 'AUDITORIA_VALOR_REDONDO', label: 'LANÇAMENTOS COM VALORES "REDONDOS" — ESTIMATIVAS MANUAIS' },
  { value: 'AUDITORIA_OUTRAS_DESPESAS', label: '"LIXÃO CONTÁBIL" — OUTRAS DESPESAS OPERACIONAIS (3.01.01.08)' },
  { value: 'AUDITORIA_ESTOQUE_SEM_MOVIMENTO', label: '"CUSTO DE OPORTUNIDADE" — ESTOQUES SEM MOVIMENTAÇÃO (≥ 180 DIAS)' },
  { value: 'AUDITORIA_SPLIT_DESPESAS', label: 'FRAGMENTAÇÃO DE DESPESAS ("SPLIT" CONTÁBIL) — 3.01.01.06 / 3.01.01.08.01' },
  { value: 'AUDITORIA_CAIXA_FORNECEDORES', label: 'PAGAMENTOS A FORNECEDORES VIA CAIXA/ESPÉCIE — 1.01.01.01 → 2.01.01.03' },
  { value: 'AUDITORIA_CIRCULARIDADE_TRANSITORIA', label: 'CIRCULARIDADE DE FLUXO EM CONTAS TRANSITÓRIAS INTER-COMPANY' },
  { value: 'AUDITORIA_CORRELACAO_FISCAL', label: 'CORRELAÇÃO RECEITA × IMPOSTOS — PIS/COFINS/ICMS/ISS (ALÍQUOTA EFETIVA)' },
  { value: 'AUDITORIA_PL_INTEGRIDADE', label: 'INTEGRIDADE DO PATRIMÔNIO LÍQUIDO — LANÇAMENTOS ATÍPICOS (PL vs CAIXA / DIA NÃO ÚTIL)' },
  { value: 'AUDITORIA_PAYROLL_DRIFT', label: 'VARIAÇÃO MÓVEL DE FOLHA — DRIFT ENCARGO × SALÁRIO (PAYROLL DRIFT ANALYTICS)' },
  { value: 'AUDITORIA_ASSET_RECOVERY', label: 'REVERSÃO DE PERDAS — CRÉDITO ANÔMALO EM CONTA DE DESPESA (ASSET RECOVERY)' },
  { value: 'AUDITORIA_SIM_CAPITAL', label: 'SIMETRIA DE CAPITAL — APORTE SEM LASTRO / RECLASSIFICAÇÃO PASSIVO→PL' },
];

// COR-172: mapa único value→label (o mesmo já usado nos <option> do seletor acima) para
// converter o identificador técnico do tributo/auditoria (ex.: "SALDOS_BANCARIOS") na
// descrição amigável exibida ao usuário (ex.: "SALDOS BANCÁRIOS") em qualquer outro lugar
// da tela que precise exibir esse valor — hoje, o painel "Contexto da Auditoria" em
// AuditoriaPage.tsx. Fonte única de verdade: nunca duplicar esta lista em outro arquivo.
const LABELS_POR_TRIBUTO: Record<string, string> = {
  TODOS: 'TODOS',
  ...Object.fromEntries([...TRIBUTOS_FISCAIS, ...OUTRAS_AUDITORIAS].map((item) => [item.value, item.label])),
};

// Fallback só entra em jogo se um novo tributo for cadastrado no enum sem entrada correspondente
// nas listas acima — evita que o identificador técnico cru (com "_") vaze para a tela.
export const obterLabelTributo = (tributo: TributoAuditoria): string =>
  LABELS_POR_TRIBUTO[tributo] ?? tributo.replace(/_/g, ' ');

const AuditoriaFiltros: React.FC<Props> = ({
  empresas,
  periodos,
  empresaId,
  periodo,
  tributo,
  onEmpresaChange,
  onPeriodoChange,
  onTributoChange,
  modoSomenteTodos = false,
  tributosPermitidos,
}) => {
  const filtrarPermitidos = (lista: { value: TributoAuditoria; label: string }[]) =>
    tributosPermitidos ? lista.filter((item) => tributosPermitidos.includes(item.value)) : lista;

  const tributosFiscais = filtrarPermitidos(TRIBUTOS_FISCAIS);
  const outrasAuditorias = filtrarPermitidos(OUTRAS_AUDITORIAS);

  const empresaOptions: IOptionEmpresa[] = empresas.map((empresa) => ({
    value: empresa.id,
    label: empresa.nomeFantasia || empresa.razaoSocial || empresa.cnpj,
  }));

  const tributoGroupedOptions: GroupBase<IOptionTributo>[] = [
    { label: 'Geral', options: [{ value: 'TODOS', label: 'TODOS' }] },
    ...(tributosFiscais.length > 0 ? [{ label: 'Tributos', options: tributosFiscais }] : []),
    ...(outrasAuditorias.length > 0 ? [{ label: 'Outras Auditorias', options: outrasAuditorias }] : []),
  ];

  const tributoSelecionado: IOptionTributo | null =
    tributo === 'TODOS'
      ? { value: 'TODOS', label: 'TODOS' }
      : [...tributosFiscais, ...outrasAuditorias].find((item) => item.value === tributo) ?? null;

  return (
    <div className='auditoria-filtros'>
      <div>
        <label className='auditoria-label'>Empresa</label>
        <Select<IOptionEmpresa>
          inputId='auditoria-select-empresa'
          classNamePrefix='react-select'
          styles={estiloSelectPesquisavel}
          options={empresaOptions}
          value={empresaOptions.find((opt) => opt.value === empresaId) ?? null}
          onChange={(selected: SingleValue<IOptionEmpresa>) => onEmpresaChange(selected?.value)}
          placeholder='Selecione a empresa'
          noOptionsMessage={() => 'Nenhuma empresa encontrada'}
          isClearable
        />
      </div>

      <div>
        <label className='auditoria-label'>Período</label>
        <select
          className='auditoria-select'
          value={periodo || ''}
          disabled={!empresaId}
          onChange={(e) => onPeriodoChange(e.target.value || undefined)}
        >
          <option value=''>Selecione o período</option>
          {periodos.map((item) => (
            <option key={`${item.esteiraId}-${item.periodo}`} value={String(item.esteiraId)}>
              {item.descricao}
            </option>
          ))}
        </select>
      </div>

      {!modoSomenteTodos && <div>
        <label className='auditoria-label'>Conta Auditada</label>
        <Select<IOptionTributo, false, GroupBase<IOptionTributo>>
          inputId='auditoria-select-tributo'
          classNamePrefix='react-select'
          styles={estiloSelectPesquisavel}
          options={tributoGroupedOptions}
          value={tributoSelecionado}
          isDisabled={!empresaId || !periodo}
          onChange={(selected: SingleValue<IOptionTributo>) => onTributoChange(selected?.value ?? '')}
          placeholder='Selecione o tipo de auditoria'
          noOptionsMessage={() => 'Nenhum tipo de auditoria encontrado'}
          isClearable
        />
      </div>}
    </div>
  );
};

export default AuditoriaFiltros;
